import { BoxProps, ColorOptions, Loader, ModBox, UiThemeProvider } from '@jfs/ui-new'
import { NavigationContainer, StackActions, useNavigation } from '@react-navigation/native'
import * as React from 'react'
import { Suspense, useCallback, useEffect, useRef } from 'react'
  import { PanResponder, TouchableOpacity, View, StyleSheet, Text, ViewStyle, TextStyle } from 'react-native' 
import { AepsAlertDialog } from './comps/AepsAlertDialog'
import { ErrorDialog } from './comps/ErrorDialog'
import { InactivityDialog } from './comps/InactivityDialog'
import { Toast } from './comps/Toast'
import { RootStore, StoreProvider, useStores } from './data/store'
import { AuthStack } from './nav/AuthStack'
import { DomesticMoneyTransferStack } from './nav/DomesticMoneyTransfer'
import { HomeStack } from './nav/HomeStack'
import { RefundStack } from './nav/Refund'
import { linking } from './nav/Screens'
import { fireAllScheduledEvent } from './network/sendEvent'
import { FallbackScreen } from './screens/FallbackScreen'
import { getWindow, nativeJSComm, nativeJSMapper } from './util/WebFns'
import { BackHandler } from './util/backHandler'
import { aepsTheme, refundTheme } from './util/customTheme'
import { Bbps } from './nav/Bbps'


const App = () => {
  const [appType, setAppType] = React.useState<any>(undefined)
  const onAppTypeCaptured = useCallback((data: any) => {
    const agentInfo = JSON.parse(data)
    const sessionData = agentInfo.SESSION_DATA
    const parsedSessionData: any = window.atob(sessionData)
    setAppType(JSON.parse(parsedSessionData)?.type?.toString() ?? '0')
    RootStore.consents.updateAppType(JSON.parse(parsedSessionData)?.type?.toString() ?? '0')
  }, [])
  useEffect(() => {
    if (getWindow().AepsWebViewInterface) {
      nativeJSComm(nativeJSMapper.getAgentInfo, onAppTypeCaptured)
    } else {
      setAppType('1')
    }
    //@ts-ignore
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  if (!appType) {
    return <Loader type={'large'} />
  }

  return (
    <StoreProvider value={RootStore}>
      <UiThemeProvider
        initialThemeName="light"
        customTheme={{
          name: 'customTheme',
          extraTheme: appType === '0' ? aepsTheme : refundTheme,
          extends: 'light',
        }}>
        <StackSwitcher />
      </UiThemeProvider>
    </StoreProvider>
  )
}

export default App
const navigationRef: any = React.createRef()

export const replaceNvigation = (name) => {
  navigationRef.current?.dispatch(StackActions.replace(name))
}

const StackSwitcher = () => {
  const { errorMessage, resetErrorDialog, invalidateSession, appType } = useStores((root) => ({
    errorMessage: root.message.getMessage(),
    resetErrorDialog: root.message.resetErrorDialog,
    invalidateSession: root.agent.session.invalidateSession,
    appType: root.consents.appType,
  }))
  const [isLoading, setIsLoading] = React.useState(false)

  const inactivityTimerId: any = useRef(false)
  const sessionExpiringTimerId: any = useRef(false)

  const [inactivityDialogInfo, setInactivityDialogInfo] = React.useState({ show: false, type: '' })

  const resetInactivityTimeout = useCallback(() => {
    clearTimeout(inactivityTimerId.current)
    inactivityTimerId.current = setTimeout(async () => {
      // action after user has been detected idle
      // ex: logout from the app
      setIsLoading(true)
      await invalidateSession()
      setIsLoading(false)
      setInactivityDialogInfo({ show: true, type: 'SESSION_EXPIRED' })
    }, 4.5 * 60 * 1000)
  }, [invalidateSession])

  const resetShowSessionExpiringTimeout = useCallback(() => {
    clearTimeout(sessionExpiringTimerId.current)
    sessionExpiringTimerId.current = setTimeout(() => {
      // action after user has been detected idle
      // ex: logout from the app
      setInactivityDialogInfo({ show: true, type: 'RESET_TIMER' })
    }, 2.5 * 60 * 1000)
  }, [])

  useEffect(() => {
    console.log('onStartShouldSetPanResponderCapture in reset')
    resetInactivityTimeout()
    resetShowSessionExpiringTimeout()
  }, [resetInactivityTimeout, resetShowSessionExpiringTimeout])

  const panResponder = useRef(
    PanResponder.create({
      onStartShouldSetPanResponderCapture: () => {
        console.log('onStartShouldSetPanResponderCapture')
        resetInactivityTimeout()
        resetShowSessionExpiringTimeout()
        return false
      },
    }),
  ).current

  return (
    <View {...panResponder.panHandlers} style={{ width: '100%', height: '100%' }}>
      <Toast
        message={errorMessage.value}
        hide={errorMessage.value === '' || errorMessage.errDialogMessage}
        position={errorMessage.position}
        type={errorMessage.type}
      />
      <ErrorDialog
        show={errorMessage?.errDialogMessage?.length > 0}
        message={errorMessage.errDialogMessage}
        type={errorMessage.errDialogType}
        resetErrorDialog={resetErrorDialog}
        navigationRef={navigationRef}
      />
      <InactivityDialog
        show={inactivityDialogInfo.show}
        message={ERRORS[inactivityDialogInfo.type]}
        type={inactivityDialogInfo.type}
        onPress={() => {
          setInactivityDialogInfo({ show: false, type: '' })
          if (inactivityDialogInfo.type === 'SESSION_EXPIRED') {
            nativeJSComm(nativeJSMapper.closeWebView)
          } else {
            resetInactivityTimeout()
            resetShowSessionExpiringTimeout()
            nativeJSComm(nativeJSMapper.resetBcSession)
          }
        }}
      />
      <ModBox show={isLoading} {...styles.loaderContainer}>
        <Loader type="large" color="PRIMARY_STORNG" />
      </ModBox>
      <NavigationContainer linking={linking} ref={navigationRef}>
        <Suspense fallback={<FallbackScreen />}>
          <Stacks appType={appType} />
        </Suspense>
      </NavigationContainer>
    </View>
  )
}

const ERRORS: any = {
  RESET_TIMER: 'Your session is going to expire in 2 mins.\n do you want to continue?',
  SESSION_EXPIRED: 'Your session has expired',
}

interface StacksInterface {
  appType: string
}

const Stacks = ({ appType }: StacksInterface) => {
  const { isAadhaarVarified, invalidateSession, getAgentLocationInfo } = useStores((root) => ({
    isAadhaarVarified: root.agent.isAadhaarVarified,
    invalidateSession: root.agent.session.invalidateSession,
    getAgentLocationInfo: root.agent.getAgentLocationInfo,
    // fetchDeviceLocation: root.agent.fetchDeviceLocation
  }))

  const navigation = useNavigation()
  const [isLoading, setIsLoading] = React.useState(false)
  const [showBackDialog, setShowBackDialog] = React.useState(false)
  const [showExitDialog, setShowExitDialog] = React.useState(false)

  useEffect(() => {
    if (isAadhaarVarified && appType === '0') {
      setTimeout(() => {
        getAgentLocationInfo()
        fireAllScheduledEvent()
      }, 1500)
    }
  }, [isAadhaarVarified, getAgentLocationInfo, appType])

  useEffect(() => {
    const backHandler = async () => {
      if (!BackHandler.isTransactionLoaderShowing) {
        if (navigation.canGoBack()) {
          navigation?.goBack()
        } else {
          setShowBackDialog(true)
        }
      }
      return 'true'
    }

    window.onBackPress = backHandler
  }, [invalidateSession, isLoading, navigation])

  const onOkPress = async () => {
    setShowBackDialog(false)
    setShowExitDialog(false)
    setIsLoading(true)
    await invalidateSession()
    setIsLoading(false)
    nativeJSComm(nativeJSMapper.closeWebView)
  }

  const onCancelPress = () => {
    setShowBackDialog(false)
    setShowExitDialog(false)
  }

  const handleExitButtonPress = () => {
    setShowExitDialog(true) 
  }
  

  const INITIATE_DOMESTIC_TRANSFER_PAYMENT = appType === '1'
  const SHOW_RETURN_FLOW = appType === '2'
  const SHOW_AEPS = appType === '0'
  const SHOW_BBPS = appType === '3'

  const getStackName = () => {
    if (SHOW_RETURN_FLOW) {
      return <RefundStack />
    } else if (INITIATE_DOMESTIC_TRANSFER_PAYMENT) {
      return <DomesticMoneyTransferStack />
    } else if (SHOW_BBPS) {
      console.log('SHOW_BBPS')
      return <Bbps />
    } else if (!isAadhaarVarified) {
      return <AuthStack />
    } else if (SHOW_AEPS) {
      return <HomeStack />
    }
  }

  const returnAppName: any = {
    '0': 'AEPS',
    '1': 'Domestic Money Transfer',
    '2': 'Refund',
    '3': 'BBPS',
  }

  return (
    <>
      {getStackName()}
      <AepsAlertDialog
        show={showBackDialog}
        text={`Are you sure you want to exit ${returnAppName[appType as string]}?`}
        onOkPress={onOkPress}
        onCancelPress={onCancelPress}
        postitiveButtonText={'OK'}
        negativeButtonText={'Cancel'}
      />
      <AepsAlertDialog
        show={showExitDialog}
        text={`Are you sure you want to exit ${returnAppName[appType as string]} and end your session?`}
        onOkPress={onOkPress}
        onCancelPress={onCancelPress}
        postitiveButtonText={'OK'}
        negativeButtonText={'Cancel'}
      />

      <ModBox show={isLoading} {...styles.loaderContainer}>
        <Loader type="large" color="PRIMARY_STORNG" />
      </ModBox>
    </>
  )
}

const styles = {
  loaderContainer: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: aepsTheme.colors.TRANSPARENT_SHADE as ColorOptions,
  } as BoxProps,
}
